import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  UserCircle,
  Building2,
  Hash,
  MapPin,
  Users,
  MessageSquare,
  Inbox,
  Sparkles,
} from "lucide-react";
import { TeachingBanner } from "../components/TeachingBanner";

/** Wave 4 item 1: /me profile snapshot.
 *
 *  A mirror Memso holds up to the user: identity, the LLM's read on
 *  who they are (qwen3:8b prose from /api/me), the people / orgs /
 *  topics / places it has woven most strongly, behaviour facets,
 *  counters, and a 90-day activity heatmap. Everything is active-view
 *  scoped server-side; the page just renders what /api/me returns
 *  against the current view. */
interface Node {
  id: string;
  label: string;
  total_strength?: number;
}
interface MeResp {
  ok: boolean;
  identity: {
    name: string | null;
    handle: string | null;
    email: string | null;
    country: unknown;
    heritage: unknown;
    role: unknown;
  };
  prose: string;
  topContacts: Node[];
  topOrgs: Node[];
  topTopics: Node[];
  topPlaces: Node[];
  facets: Array<{ facet_kind: string; summary: string }>;
  stats: {
    people: number;
    orgs: number;
    conversations: number;
    packets: number;
    engagements: number;
  };
  heat: Array<{ dow: number; hr: number; n: number }>;
}

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function str(v: unknown): string | null {
  if (v == null) return null;
  if (typeof v === "string") return v;
  return String(v);
}

/** Coerce whatever /api/me returns into a fully-formed MeResp with
 *  safe defaults. A fresh account, a degraded backend, or a payload
 *  shape drift (e.g. an auth probe leaking onto this route) must never
 *  crash the render path with `undefined.map` / `undefined.length`.
 *  Missing pieces simply render as the zero-state. */
function normalizeMe(raw: unknown): MeResp {
  const d = (raw ?? {}) as Partial<MeResp> & Record<string, unknown>;
  const id = (d.identity ?? {}) as Partial<MeResp["identity"]>;
  const s = (d.stats ?? {}) as Partial<MeResp["stats"]>;
  const arr = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
  const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);
  return {
    ok: d.ok !== false,
    identity: {
      name: id.name ?? null,
      handle: id.handle ?? null,
      email: id.email ?? null,
      country: id.country ?? null,
      heritage: id.heritage ?? null,
      role: id.role ?? null,
    },
    prose: typeof d.prose === "string" ? d.prose : "",
    topContacts: arr<Node>(d.topContacts),
    topOrgs: arr<Node>(d.topOrgs),
    topTopics: arr<Node>(d.topTopics),
    topPlaces: arr<Node>(d.topPlaces),
    facets: arr<MeResp["facets"][number]>(d.facets),
    stats: {
      people: num(s.people),
      orgs: num(s.orgs),
      conversations: num(s.conversations),
      packets: num(s.packets),
      engagements: num(s.engagements),
    },
    heat: arr<MeResp["heat"][number]>(d.heat),
  };
}

export function Me() {
  const [data, setData] = useState<MeResp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setData(null);
    // Hard ceiling so a slow or wedged backend can never leave this
    // page stuck on "Loading…" forever. On timeout we surface a
    // recoverable error instead of an infinite spinner.
    const ctrl = new AbortController();
    const timer = window.setTimeout(() => ctrl.abort(), 15_000);
    fetch("/api/me", { credentials: "same-origin", signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => {
        if (!cancelled) setData(normalizeMe(d));
      })
      .catch((e) => {
        if (cancelled) return;
        const aborted = e instanceof Error && e.name === "AbortError";
        setError(
          aborted
            ? "This took too long to load. Memso may still be warming up."
            : e instanceof Error
              ? e.message
              : String(e),
        );
      })
      .finally(() => window.clearTimeout(timer));
    return () => {
      cancelled = true;
      ctrl.abort();
      window.clearTimeout(timer);
    };
  }, [reloadKey]);

  if (error)
    return (
      <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-6 space-y-3">
        <div className="rounded-lg border border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-900/30 px-4 py-3 text-sm text-rose-800 dark:text-rose-200">
          Could not load your profile: {error}
        </div>
        <button
          type="button"
          onClick={() => setReloadKey((k) => k + 1)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700"
        >
          Try again
        </button>
      </div>
    );
  if (!data)
    return (
      <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-10 text-sm text-slate-400">
        Loading…
      </div>
    );

  const wovenAnything =
    data.stats.packets > 0 ||
    data.stats.people > 0 ||
    data.topContacts.length > 0 ||
    data.topOrgs.length > 0 ||
    data.topTopics.length > 0 ||
    data.topPlaces.length > 0;

  if (!wovenAnything) {
    return (
      <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-6 space-y-6">
        <header className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-sky-400 to-indigo-500 flex items-center justify-center text-white shadow-md flex-shrink-0">
            <UserCircle className="w-8 h-8" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100 truncate">
              {data.identity.name ?? "You"}
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
              {[data.identity.handle && `@${data.identity.handle}`, data.identity.email]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>
        </header>

        <section className="glass rounded-2xl border border-sky-200/70 dark:border-sky-800/40 backdrop-blur bg-gradient-to-br from-sky-50/60 via-white/30 to-indigo-50/40 dark:from-sky-900/15 dark:via-slate-800/20 dark:to-indigo-900/10 p-6 sm:p-8">
          <div className="flex items-center gap-2 text-sm font-semibold text-sky-700 dark:text-sky-300 mb-2">
            <Sparkles className="w-4 h-4" />
            This is your mirror
          </div>
          <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed max-w-prose">
            As Memso captures and weaves your email, calendar, and notes, this
            page fills in: the people you are closest to, the orgs and topics
            that recur, the places you move through, and a rhythm of when you
            are most active. Nothing here is woven yet. Connect a source or
            capture a thought and your mirror starts to take shape.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Link
              to="/settings?section=connectors"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold bg-violet-600 text-white shadow-sm hover:bg-violet-700 transition-colors"
            >
              Connect a source
            </Link>
            <Link
              to="/capture"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-white/60 dark:hover:bg-slate-800/40 transition-colors"
            >
              Capture a thought
            </Link>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 p-5">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2">
            What Memso already knows
          </h2>
          <dl className="text-sm text-slate-600 dark:text-slate-300 space-y-1.5">
            {data.identity.name && (
              <div className="flex gap-2">
                <dt className="w-20 text-slate-400">Name</dt>
                <dd>{data.identity.name}</dd>
              </div>
            )}
            {data.identity.handle && (
              <div className="flex gap-2">
                <dt className="w-20 text-slate-400">Handle</dt>
                <dd>@{data.identity.handle}</dd>
              </div>
            )}
            {data.identity.email && (
              <div className="flex gap-2">
                <dt className="w-20 text-slate-400">Email</dt>
                <dd>{data.identity.email}</dd>
              </div>
            )}
            <p className="text-xs text-slate-400 pt-1">
              From the account you signed in with. Everything else on this
              page is woven from what you capture.
            </p>
          </dl>
        </section>
      </div>
    );
  }

  const maxHeat = Math.max(1, ...data.heat.map((h) => h.n));
  const heatAt = (dow: number, hr: number) =>
    data.heat.find((h) => h.dow === dow && h.hr === hr)?.n ?? 0;

  const role = str(data.identity.role);
  const country = str(data.identity.country);
  const heritage = str(data.identity.heritage);

  return (
    <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-6 space-y-6">
      <header className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-sky-400 to-indigo-500 flex items-center justify-center text-white shadow-md flex-shrink-0">
          <UserCircle className="w-8 h-8" />
        </div>
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100 truncate">
            {data.identity.name ?? "You"}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
            {[data.identity.handle && `@${data.identity.handle}`, data.identity.email, role]
              .filter(Boolean)
              .join(" · ")}
          </p>
          {(country || heritage) && (
            <p className="text-xs text-slate-400 mt-0.5">
              {[country, heritage].filter(Boolean).join(" · ")}
            </p>
          )}
        </div>
      </header>

      <TeachingBanner id="me_overview" />

      {data.prose && (
        <section className="glass rounded-2xl border border-sky-200/70 dark:border-sky-800/40 backdrop-blur bg-gradient-to-br from-sky-50/60 via-white/30 to-indigo-50/40 dark:from-sky-900/15 dark:via-slate-800/20 dark:to-indigo-900/10 p-5 sm:p-6">
          <div className="flex items-center gap-2 text-sm font-semibold text-sky-700 dark:text-sky-300 mb-2">
            <Sparkles className="w-4 h-4" />
            What Memso has learned
          </div>
          <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
            {data.prose}
          </p>
        </section>
      )}

      <section className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {([
          ["People", data.stats.people, Users],
          ["Orgs", data.stats.orgs, Building2],
          ["Conversations", data.stats.conversations, MessageSquare],
          ["Captures", data.stats.packets, Inbox],
          ["Engagements", data.stats.engagements, Hash],
        ] as Array<[string, number, typeof Users]>).map(([label, v, Icon]) => (
          <div
            key={label}
            className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 p-3 text-center"
          >
            <Icon className="w-4 h-4 mx-auto text-slate-400 mb-1" />
            <div className="text-xl font-bold text-slate-900 dark:text-slate-100">
              {v}
            </div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400">
              {label}
            </div>
          </div>
        ))}
      </section>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <NodeList
          title="Closest people"
          Icon={Users}
          nodes={data.topContacts}
          route={(n) => `/people/${encodeURIComponent(n.id)}`}
        />
        <NodeList
          title="Top orgs"
          Icon={Building2}
          nodes={data.topOrgs}
          route={(n) => `/orgs/${encodeURIComponent(n.id)}`}
        />
        <NodeList title="Recurring topics" Icon={Hash} nodes={data.topTopics} />
        <NodeList title="Places" Icon={MapPin} nodes={data.topPlaces} />
      </div>

      {data.facets.length > 0 && (
        <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 p-5 space-y-2.5">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            Behaviour facets
          </h2>
          <ul className="space-y-1.5">
            {data.facets.map((f, i) => (
              <li key={i} className="text-sm text-slate-600 dark:text-slate-300">
                <span className="text-[11px] uppercase tracking-wider font-bold text-slate-400 mr-2">
                  {f.facet_kind}
                </span>
                {f.summary}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 p-5">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">
          Activity rhythm <span className="text-slate-400 font-normal">(last 90 days)</span>
        </h2>
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full">
            {DOW.map((d, dow) => (
              <div key={dow} className="flex items-center gap-1 mb-1">
                <span className="w-8 text-[10px] text-slate-400 flex-shrink-0">
                  {d}
                </span>
                <div className="flex gap-0.5">
                  {Array.from({ length: 24 }, (_, hr) => {
                    const n = heatAt(dow, hr);
                    const intensity = n === 0 ? 0 : 0.18 + 0.82 * (n / maxHeat);
                    return (
                      <div
                        key={hr}
                        title={`${d} ${hr}:00 — ${n} captures`}
                        className="w-2.5 h-2.5 rounded-[2px]"
                        style={{
                          backgroundColor:
                            n === 0
                              ? "rgba(148,163,184,0.15)"
                              : `rgba(14,165,233,${intensity})`,
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function NodeList({
  title,
  Icon,
  nodes,
  route,
}: {
  title: string;
  Icon: typeof Users;
  nodes: Node[];
  route?: (n: Node) => string;
}) {
  return (
    <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 p-5">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2.5">
        <Icon className="w-4 h-4 text-slate-400" />
        {title}
      </h2>
      {nodes.length === 0 ? (
        <p className="text-xs text-slate-400 italic">
          Nothing woven in this view yet.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {nodes.map((n) =>
            route ? (
              <li key={n.id}>
                <Link
                  to={route(n)}
                  className="text-sm text-sky-600 dark:text-sky-400 hover:underline truncate block"
                >
                  {n.label}
                </Link>
              </li>
            ) : (
              <li
                key={n.id}
                className="text-sm text-slate-700 dark:text-slate-200 truncate"
              >
                {n.label}
              </li>
            ),
          )}
        </ul>
      )}
    </section>
  );
}
